import { z } from "zod";

export const LEGAL_MARKDOWN_MAX = 50_000;

export const legalMarkdownSchema = z.object({
  de: z.string().max(LEGAL_MARKDOWN_MAX),
  en: z.string().max(LEGAL_MARKDOWN_MAX),
});

export type LegalMarkdown = z.infer<typeof legalMarkdownSchema>;

export const DEFAULT_LEGAL_PRIVACY: LegalMarkdown = {
  de: `# Datenschutz

Stand: 12.10.2026

Diese Erklärung gilt für die Grantonomy-Zeiterfassungsanwendung (diese Website), nicht für die Marketing-Website [grantonomy.de](https://grantonomy.de).

## 1. Verantwortlicher

Verantwortlich für die Verarbeitung personenbezogener Daten im Zusammenhang mit dieser Anwendung ist die **Grantonomy GmbH**.

Weitere Angaben zum Unternehmen finden Sie im [Impressum](https://grantonomy.de). Ein Datenschutzbeauftragter ist nicht bestellt.

## 2. Verantwortlicher und Auftragsverarbeiter

- Für den Besuch der öffentlichen Seiten und für Konten von Mitarbeitenden der Grantonomy GmbH ist die Grantonomy GmbH Verantwortliche im Sinne der DSGVO.
- Für Arbeitszeit-, Abwesenheits- und F&E-Daten der Mitarbeitenden eines Kundenunternehmens ist in der Regel das Kundenunternehmen Verantwortlicher. Die Grantonomy GmbH verarbeitet diese Daten als Auftragsverarbeiterin (Art. 28 DSGVO), um eine GoBD-konforme Dokumentation der Forschungszulage (FZulG) zu ermöglichen.

## 3. Welche Daten wir verarbeiten

- Stammdaten: Vorname, Nachname, Konto-E-Mail-Adresse, Rolle, Position, Tätigkeitsbeschreibung
- Arbeitsprofil: Wochenstunden, Arbeitstage der Woche
- Zeiterfassung: Tagesstunden (Gesamt), Abwesenheiten, F&E-Stunden je Projekt, Wochennotizen
- Projekt- und Unternehmensbezeichnungen, Zuordnungen und Eintrittsdaten
- Integrität: unveränderliches Prüfprotokoll (vollständige Datensatz-Abzüge bei Änderungen)
- Sicherheits- und Zugriffsprotokoll: Anmeldung, Exporte, administrative Änderungen, einschließlich IP-Adresse
- Anmeldetoken: nur als Hash, mit IP bei Ausstellung, befristet
- Technische Versandprotokolle (Einladungen, Anmeldelinks, Erinnerungen)
- Spracheinstellung im lokalen Speicher des Browsers

Wir erheben keine Analyse- oder Werbekennungen, keine Social-Logins, keinen Zugriff auf Kamera oder Kontakte und keine automatisierte KI-Auswertung Ihrer Zeiten. Es gibt kein Instagram-Plugin und keine Web-Analyse in dieser Anwendung.

## 4. Herkunft der Daten

Daten geben Sie selbst ein, oder Ihr Unternehmen legt sie an (Einladung, Import). Technische Daten entstehen bei der Nutzung (Sitzung, Protokolle, IP-Adresse).

## 5. Zwecke und Rechtsgrundlagen

- Erbringung der vertraglichen Zeiterfassung (Art. 6 Abs. 1 lit. b DSGVO)
- Gesetzliche Aufbewahrung und GoBD-/steuerliche Dokumentation einschließlich FZulG (Art. 6 Abs. 1 lit. c DSGVO)
- IT-Sicherheit, Missbrauchsschutz, Ratenbegrenzung und revisionssichere Audit-Kette (Art. 6 Abs. 1 lit. f DSGVO)
- Beschäftigtendaten beim Arbeitgeber nach § 26 BDSG; Grantonomy handelt insoweit weisungsgebunden als Auftragsverarbeiterin

Die Nutzung setzt keine Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO voraus. Das essenzielle Sitzungs-Cookie stützt sich auf § 25 Abs. 2 Nr. 2 TDDDG (unbedingt erforderlich).

## 6. Cookies und lokale Speicherung

Nur ein essenzielles Sitzungs-Cookie (\`grantonomy_session\`): HttpOnly, SameSite=Lax, in der Produktion Secure, Laufzeit 14 Tage. Inhalt: Benutzerkennung und Gültigkeit, kryptographisch gesichert. Keine Marketing-, Tracking- oder Analyse-Cookies.

Die gewählte Sprache wird im lokalen Speicher des Browsers gehalten (\`grantonomy.lang\`).

Es gibt kein Cookie-Banner, weil keine nicht erforderlichen Cookies gesetzt werden.

## 7. Empfänger

Zugriff nur rollenbasiert: Mitarbeitende sehen eigene Daten, die Unternehmensadministration das eigene Unternehmen, die Grantonomy-Administration den Betrieb der Plattform (Support und Dokumentation).

Externe Auftragsverarbeiter:

- Railway (Hosting der Anwendung und der Datenbank, Region EU)
- Google (Zustellung transaktionaler Nachrichten über Google Workspace)

Es findet kein Verkauf personenbezogener Daten statt.

## 8. Speicherdauer

Zeiterfassung und das Prüfprotokoll werden für GoBD, FZulG und die Vertragsbeziehung mit dem Auftraggeber vorgehalten. Das Prüfprotokoll ist in der Anwendung nicht löschbar.

Anmeldetoken werden nach Ablauf oder Verwendung zeitnah entfernt. Technische Versandwarteschlangen werden nach etwa 30 Tagen bereinigt. Zugriffsprotokolle bleiben für die Nachvollziehbarkeit erhalten.

## 9. Sicherheit

Kennwörter und einmalige Anmeldelinks werden nur als Hash gespeichert. Die Sitzung ist signiert. Schreibvorgänge an Zeitdaten werden mit Akteurkontext protokolliert.

## 10. Ihre Rechte

Sie haben die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch nach Art. 15 bis 21 DSGVO, soweit die gesetzlichen Voraussetzungen vorliegen.

Es gibt in dieser Anwendung keine Selbstbedienung für Auskunft oder Löschung. Bitte wenden Sie sich an die Administration Ihres Unternehmens oder an die Grantonomy GmbH über das [Impressum](https://grantonomy.de).

Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren, insbesondere bei der Landesbeauftragten für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW).

## 11. Widerspruch (Art. 21 DSGVO)

Soweit die Verarbeitung auf Art. 6 Abs. 1 lit. f DSGVO beruht, können Sie aus Gründen, die sich aus Ihrer besonderen Situation ergeben, widersprechen. Wir prüfen dann, ob schutzwürdige Gründe überwiegen.

## 12. Automatisierte Entscheidungen

Es findet keine automatisierte Entscheidungsfindung einschließlich Profiling im Sinne von Art. 22 DSGVO statt.

## 13. Änderungen

Wir passen diese Erklärung an, wenn sich Verarbeitung oder Rechtslage ändern. Maßgeblich ist die auf dieser Seite veröffentlichte Fassung.
`,
  en: `# Privacy Policy

Last updated: 12 October 2026

This notice applies to the Grantonomy time-tracking application (this website), not to the marketing site [grantonomy.de](https://grantonomy.de).

## 1. Controller

The controller for personal data processed in connection with this application is **Grantonomy GmbH**.

Further company details are in the [legal notice (Impressum)](https://grantonomy.de). No data protection officer has been appointed.

## 2. Controller and processor

- For visits to the public pages and for accounts of Grantonomy GmbH staff, Grantonomy GmbH is the controller under the GDPR.
- For working-time, absence and R&D data of a client company’s employees, that client company is typically the controller. Grantonomy GmbH processes those data as a processor (Art. 28 GDPR) to provide GoBD-grade documentation for the German research allowance (FZulG).

## 3. Data we process

- Master data: first name, last name, account email address, role, position, job description
- Work profile: weekly hours, weekdays worked
- Time tracking: daily totals, absences, R&D hours per project, week notes
- Company and project names, assignments and entry dates
- Integrity: append-only audit trail (full row snapshots on changes)
- Security and access log: sign-in, exports, administrative changes, including IP address
- Sign-in tokens: stored as a hash only, with IP at issuance, time-limited
- Technical delivery logs (invites, sign-in links, reminders)
- Language preference in the browser’s local storage

We do not collect analytics or advertising identifiers, social logins, camera or contacts access, or automated AI evaluation of your hours. This application has no Instagram plugin and no web analytics.

## 4. Where data come from

You enter data yourself, or your company creates them (invite, import). Technical data arise from use (session, logs, IP address).

## 5. Purposes and legal bases

- Providing the contracted time-tracking service (Art. 6(1)(b) GDPR)
- Statutory retention and GoBD / tax documentation including FZulG (Art. 6(1)(c) GDPR)
- IT security, abuse prevention, rate limiting and an audit-proof trail (Art. 6(1)(f) GDPR)
- Employment data at the employer under § 26 BDSG; Grantonomy acts on instructions as processor

Use of this application does not require consent under Art. 6(1)(a) GDPR. The essential session cookie relies on § 25(2) no. 2 TDDDG (strictly necessary).

## 6. Cookies and local storage

Only one essential session cookie (\`grantonomy_session\`): HttpOnly, SameSite=Lax, Secure in production, 14-day lifetime. Contents: user id and validity, cryptographically signed. No marketing, tracking or analytics cookies.

The selected language is kept in the browser’s local storage (\`grantonomy.lang\`).

There is no cookie banner because no non-essential cookies are set.

## 7. Recipients

Access is role-based: employees see their own data, company admins see their company, Grantonomy admins operate the platform (support and documentation).

External processors:

- Railway (application and database hosting, EU region)
- Google (transactional messages via Google Workspace)

We do not sell personal data.

## 8. Retention

Time records and the audit trail are kept for GoBD, FZulG and the contract with the client. The audit trail cannot be deleted in the application.

Sign-in tokens are removed shortly after expiry or use. Technical send queues are purged after about 30 days. Access logs are kept for accountability.

## 9. Security

Passwords and one-time sign-in links are stored as hashes only. The session is signed. Writes to time data are logged with actor context.

## 10. Your rights

You have the rights of access, rectification, erasure, restriction, portability and objection under Art. 15 to 21 GDPR where the legal conditions are met.

This application has no self-service for access or deletion. Please contact your company’s administration or Grantonomy GmbH via the [legal notice (Impressum)](https://grantonomy.de).

You may lodge a complaint with a supervisory authority, in particular the North Rhine-Westphalia Commissioner for Data Protection and Freedom of Information (LDI NRW).

## 11. Right to object (Art. 21 GDPR)

Where processing is based on Art. 6(1)(f) GDPR, you may object on grounds relating to your particular situation. We will then assess whether compelling legitimate grounds override yours.

## 12. Automated decisions

There is no automated decision-making including profiling within the meaning of Art. 22 GDPR.

## 13. Changes

We update this notice when processing or the law changes. The version published on this page applies.
`,
};

export const DEFAULT_LEGAL_TERMS: LegalMarkdown = {
  de: `# Nutzungsbedingungen

Stand: 17.08.2026

## 1. Anbieter

Anbieter dieser Anwendung ist die **Grantonomy GmbH**. Weitere Angaben zum Unternehmen finden Sie im [Impressum](https://grantonomy.de).

Diese Bedingungen gelten nur für die Zeiterfassungsanwendung. Sie ersetzen nicht einen Beratungs- oder Auftragsverarbeitungsvertrag mit Ihrem Unternehmen.

## 2. Leistung

Grantonomy stellt eine B2B-Zeiterfassung für die Dokumentation der deutschen Forschungszulage bereit: Stunden, Abwesenheiten, F&E-Anteile, Monatssperren und revisionssichere Exporte.

Konten werden nur von Administratoren angelegt. Es gibt keine Selbstregistrierung.

## 3. Nutzung

Sie dürfen die Anwendung nur im Rahmen der Ihnen zugewiesenen Rolle nutzen. Einmalige Anmeldelinks und Kennwörter dürfen nicht weitergegeben werden.

Stunden, Abwesenheiten und F&E-Anteile sind wahrheitsgemäß zu erfassen. Exporte enthalten nur tatsächlich erfasste Werte, keine synthetischen Stunden.

Monatssperren (Festschreibung) und das unveränderliche Prüfprotokoll dürfen nicht umgangen werden.

## 4. Verfügbarkeit

Wir bemühen uns um einen störungsfreien Betrieb. Eine bestimmte Verfügbarkeit wird in diesen Bedingungen nicht zugesichert.

## 5. Haftung

Die Grantonomy GmbH haftet unbeschränkt bei Vorsatz, grober Fahrlässigkeit und bei Verletzung von Leben, Körper oder Gesundheit.

Bei leichter Fahrlässigkeit haftet sie nur bei Verletzung wesentlicher Vertragspflichten und begrenzt auf den vorhersehbaren, typischen Schaden, soweit gesetzlich zulässig.

## 6. Auftragsverarbeitung

Arbeitszeitdaten von Mitarbeitenden eines Kundenunternehmens verarbeitet Grantonomy im Auftrag dieses Unternehmens. Einzelheiten ergeben sich aus der Vereinbarung zur Auftragsverarbeitung mit dem Kundenunternehmen.

## 7. Änderungen

Wir können diese Bedingungen anpassen, soweit das für den Betrieb der Anwendung erforderlich ist. Die auf dieser Seite veröffentlichte Fassung gilt für die weitere Nutzung.

## 8. Recht

Es gilt das Recht der Bundesrepublik Deutschland. Soweit zulässig, ist Gerichtsstand Düsseldorf.
`,
  en: `# Terms and Conditions

Last updated: 17 August 2026

## 1. Provider

The provider of this application is **Grantonomy GmbH**. Further company details are in the [legal notice (Impressum)](https://grantonomy.de).

These terms apply only to the time-tracking application. They do not replace a consulting agreement or a data-processing agreement with your company.

## 2. Service

Grantonomy provides B2B time tracking for documenting the German research allowance: hours, absences, R&D shares, month locks and audit-ready exports.

Accounts are created by administrators only. There is no self-signup.

## 3. Use

You may use the application only in the role assigned to you. Do not share one-time sign-in links or passwords.

Hours, absences and R&D shares must be recorded truthfully. Exports contain actually tracked values only, not synthetic hours.

Do not bypass month locks (Festschreibung) or the append-only audit trail.

## 4. Availability

We aim for uninterrupted operation. These terms do not guarantee a particular uptime.

## 5. Liability

Grantonomy GmbH is fully liable for intent, gross negligence and injury to life, body or health.

For slight negligence it is liable only for breach of material contractual duties and, where legally permitted, limited to the foreseeable typical damage.

## 6. Processing on behalf

Working-time data of a client company’s employees is processed by Grantonomy on that company’s behalf. Details are in the data-processing agreement with the client company.

## 7. Changes

We may update these terms where needed to operate the application. The version published on this page applies to continued use.

## 8. Law

The law of the Federal Republic of Germany applies. Where permitted, the place of jurisdiction is Düsseldorf.
`,
};
